import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import Circle from 'react-circle';
import Tooltip from 'shared/components/Tooltip';
import getScoreColor from 'shared/lib/getScoreColor';
import styles from './Score.module.scss';

const ignoredTextColor = '#fb236a';
const ignoredBgColor = '#ffc6c1';

const Score = ({
  id,
  score,
  lineWidth,
  active,
  activateLink,
  type,
  className,
  inactiveJob,
  ignored,
  featureDisabled
}) => {
  const { t } = useTranslation('common');

  const tooltip = useMemo(() => {
    if (featureDisabled) {
      return t('backoffice:subscription.featureNotAllowed');
    }

    if (!active) {
      return t('score.loginToDisplay', { scoreName: t('scoreName') });
    }

    if (inactiveJob) {
      return t('userportal:page.job.jobIsNotActive');
    }

    if (ignored) {
      return t('score.ignored');
    }

    return t(`score.${type}`, { scoreName: t('scoreName'), score: score || 0 });
  }, [featureDisabled, ignored, inactiveJob, type, score]);

  return (
    <Tooltip content={tooltip}>
      <div
        id={`score-${id}`}
        className={cn(styles.score, {
          [styles.inactive]: !active || featureDisabled,
        }, className)}
      >
        {(!active && activateLink !== null) && (
          <div className={styles.login}>
            <Link id={`score-${id}-link`} to={activateLink}>?</Link>
          </div>
        )}
        {featureDisabled && (
          <div className={styles.login}>
            <span>?</span>
          </div>
        )}
        <Circle
          bgColor={ignored ? ignoredBgColor : undefined}
          progress={score || 0}
          progressColor={getScoreColor(score)}
          textColor={ignored ? ignoredTextColor : getScoreColor(score)}
          responsive
          lineWidth={lineWidth}
        />
      </div>
    </Tooltip>
  );
};

Score.propTypes = {
  id: PropTypes.string,
  score: PropTypes.number,
  lineWidth: PropTypes.number,
  active: PropTypes.bool,
  activateLink: PropTypes.string,
  type: PropTypes.oneOf(['your', 'user', 'common']),
  className: PropTypes.string,
  inactiveJob: PropTypes.bool,
  ignored: PropTypes.bool,
  featureDisabled: PropTypes.bool,
};

Score.defaultProps = {
  id: 'none',
  score: 0,
  lineWidth: 50,
  active: true,
  activateLink: null,
  type: 'your',
  className: null,
  inactiveJob: false,
  ignored: false,
  featureDisabled: false,
};

export default Score;
